import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import { IconBrandSlack, IconLink, IconMail } from "@tabler/icons-react";
import { createShareLink, getShareLinks, deleteShareLink } from "@/helpers/apis";
import { ShareAccessType, ShareLink } from "./types";

export interface ShareDialogProps {
  sessionId: string;
  sessionName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EXPIRATION_OPTIONS = [
  { label: "1 day", value: "1" },
  { label: "7 days", value: "7" },
  { label: "30 days", value: "30" },
  { label: "90 days", value: "90" },
  { label: "Never", value: "never" },
];

export const ACCESS_TYPE_OPTIONS: {
  value: ShareAccessType;
  icon: "world" | "users" | "building";
  label: string;
  description: string;
}[] = [
  {
    value: "anyone",
    icon: "world",
    label: "Anyone with the link",
    description: "No sign-in required to view",
  },
  {
    value: "all_altimate_users",
    icon: "building",
    label: "Anyone in this organization",
    description: "All signed-in members of this tenant can view",
  },
  {
    value: "team",
    icon: "users",
    label: "Team",
    description: "Only selected teams can view (sign-in required)",
  },
  {
    value: "specific_people",
    icon: "users",
    label: "Specific people",
    description: "Only selected users can view (sign-in required)",
  },
];

// Tab list + URL-param helpers for ShareDialog. Lives here (not in the
// dialog component) so the dialog file stays under the max-lines lint
// budget.
export type ShareDialogTab = "link" | "slack" | "email";

export const SHARE_TAB_PARAM = "shareTab";

export const SHARE_TAB_ITEMS: {
  value: ShareDialogTab;
  label: string;
  icon: typeof IconLink;
}[] = [
  { value: "link", label: "Link", icon: IconLink },
  { value: "slack", label: "Slack", icon: IconBrandSlack },
  { value: "email", label: "Email", icon: IconMail },
];

export const resolveShareTab = (v: string | null): ShareDialogTab =>
  v === "slack" || v === "email" ? v : "link";

export const useShareDialog = (sessionId: string, _sessionName: string, open: boolean) => {
  const queryClient = useQueryClient();
  const [accessType, setAccessType] = useState<ShareAccessType>("anyone");
  const [expiresInDays, setExpiresInDays] = useState<string>("30");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);

  // Fetch existing share links
  const { data: shareLinksData, isLoading: isLoadingLinks } = useQuery({
    queryKey: ["shareLinks", sessionId],
    queryFn: () => getShareLinks(sessionId),
    enabled: open,
  });

  // Create share link mutation
  const { mutate: createLink, isPending: isCreating } = useMutation({
    mutationFn: () =>
      // Don't send `title`: the backend column is VARCHAR(255), so an
      // auto-derived `sessionName` (which can be the full user query —
      // e.g. an alert prompt of 400+ chars) trips Pydantic's max_length
      // validator and the whole create-link request fails. The shared
      // page (`SharedPlayground.tsx`) already falls back to
      // `session_name` when `share_title` is null, so the recipient
      // sees the same string either way — no UI duplication, no
      // information loss. AI-6421.
      createShareLink({
        session_id: sessionId,
        access_type: accessType,
        expires_in_days: expiresInDays === "never" ? null : parseInt(expiresInDays),
        user_ids: accessType === "specific_people" ? selectedUserIds.map(Number) : undefined,
        team_ids: accessType === "team" ? selectedTeamIds.map(Number) : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shareLinks", sessionId] });
      toast({ title: "Share link created successfully" });
      if (accessType === "specific_people") setSelectedUserIds([]);
      if (accessType === "team") setSelectedTeamIds([]);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create share link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete share link mutation
  const { mutate: removeLink, isPending: isDeleting } = useMutation({
    mutationFn: (shareId: string) => deleteShareLink(shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shareLinks", sessionId] });
      toast({ title: "Share link deleted" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete share link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const formatExpiration = (expiresAt: string | null) => {
    if (!expiresAt) return "Never expires";
    const date = new Date(expiresAt);
    return `Expires ${date.toLocaleDateString()}`;
  };

  // The radios are rendered from ACCESS_TYPE_OPTIONS, so the incoming
  // value is always a valid ShareAccessType.
  const handleAccessTypeChange = (v: string) => {
    const next = v as ShareAccessType;
    setAccessType(next);
    if (next !== "specific_people") setSelectedUserIds([]);
    if (next !== "team") setSelectedTeamIds([]);
  };

  // Reset selections when the dialog closes so picks from one session
  // don't leak into the next open. The hook is mounted by the parent
  // for the dialog's whole lifecycle, so without this the state
  // persists across open → close → open cycles.
  useEffect(() => {
    if (open) return;
    setAccessType("anyone");
    setSelectedUserIds([]);
    setSelectedTeamIds([]);
  }, [open]);

  const canCreate =
    accessType === "anyone" ||
    accessType === "all_altimate_users" ||
    (accessType === "specific_people" && selectedUserIds.length > 0) ||
    (accessType === "team" && selectedTeamIds.length > 0);
  const shareLinks: ShareLink[] = shareLinksData?.share_links || [];

  return {
    accessType,
    handleAccessTypeChange,
    expiresInDays,
    setExpiresInDays,
    selectedUserIds,
    setSelectedUserIds,
    selectedTeamIds,
    setSelectedTeamIds,
    isLoadingLinks,
    isCreating,
    isDeleting,
    createLink,
    removeLink,
    formatExpiration,
    canCreate,
    shareLinks,
  };
};
